import { AxiosError } from 'axios';
import { TableActionTypes } from 'web-library';
import axiosClient from '../axiosClient';
import { POST_GROUP_EXAM_REGISTRATION_REVIEW } from '../../constants/apiUrls';
import { useCustomMutation } from '../utils/useCustomMutation';
import { ExamGroupExamLevelFeeResponse, ExamPayment, GenericErrorResponse } from '../typings';
import { PaginatedData } from '../../models/List';
import { ApiPathParam } from '../../models/SearchParamSchema';
import { CandidateListBase } from './model';

// =============================================================================
// Request & Response Model
// =============================================================================
export enum RegistrationRequestTab {
  CANDIDATE_LIST = 'candidateList',
  SUBJECT_LIST = 'subjectList',
}

export interface PostReviewGroupExamRegistrationRequest {
  applicationUuid: string;
  apiPathParam: ApiPathParam;
  tab: RegistrationRequestTab;
}

export interface CandidateList extends CandidateListBase {
  examLevel: string[];
  feePayable: string;
  examRegistrationUuid: string;
}

export interface SubjectList {
  subjectName: string;
  subjectCode: string;
  subjectLevel: string;
  examSeries: string;
  examLevel: string;
  language: string;
  candidateList: CandidateListBase[];
}

export enum ExamPaymentModeKey {
  bankTransferOption = 'bankTransferOption',
  candidatePayOption = 'candidatePayOption',
  coordinatorPayOption = 'coordinatorPayOption',
  noPaymentModeSelected = 'noPaymentModeSelected',
}

export interface ExamPaymentMode {
  [ExamPaymentModeKey.bankTransferOption]: boolean;
  [ExamPaymentModeKey.candidatePayOption]: boolean;
  [ExamPaymentModeKey.coordinatorPayOption]: boolean;
  [ExamPaymentModeKey.noPaymentModeSelected]?: boolean;
}

export interface ReviewGroupExamRegistrationBaseResponse {
  applicationId: string;
  coordinatorName: string;
  examYear: number;
  examLevel: string[];
  groupedLevelFees: ExamGroupExamLevelFeeResponse[];
  payment: ExamPaymentMode & ExamPayment;
  gstRegNo?: string | null;
}

export interface PostReviewGroupExamRegistrationResponse
  extends ReviewGroupExamRegistrationBaseResponse {
  candidateList?: PaginatedData<CandidateList[]>;
  subjectList?: PaginatedData<SubjectList[]>;
}

// =============================================================================
// Mutation
// =============================================================================
interface Input {
  onSuccess?: () => void;
  defaultOnSuccessInput: {
    getCandidateOrSubjectType: RegistrationRequestTab;
    tableDispatch: React.Dispatch<TableActionTypes>;
  };
  onError?: (data: AxiosError<GenericErrorResponse>) => void;
  onSettled?: () => void;
}

export const usePostReviewGroupExamRegistration = ({
  onSuccess,
  onError,
  onSettled,
  defaultOnSuccessInput,
}: Input) => {
  const { getCandidateOrSubjectType, tableDispatch } = defaultOnSuccessInput;
  const postGroupExamRegistrationReview = async (
    payload: PostReviewGroupExamRegistrationRequest
  ) => {
    const res = await axiosClient.post<PostReviewGroupExamRegistrationResponse>(
      POST_GROUP_EXAM_REGISTRATION_REVIEW(payload.applicationUuid, payload.apiPathParam),
      {
        tab: payload.tab,
      }
    );
    return res.data;
  };

  return useCustomMutation(postGroupExamRegistrationReview, {
    onSuccess: (data: PostReviewGroupExamRegistrationResponse) => {
      const paginatedData = data[getCandidateOrSubjectType] as PaginatedData<CandidateList[]>;
      tableDispatch(({
        type: 'UPDATE_DATA',
        payload: {
          data: paginatedData.content,
          count: paginatedData.totalElements,
        },
      } as unknown) as TableActionTypes);
      onSuccess?.();
    },
    onError,
    onSettled,
  });
};
